import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight, MapPin, Phone, Clock, Star } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cb900e0f-c443-41f6-9df3-17a2db93c215/artifacts/qd501ira_D70BB6EE-7344-46BE-86BC-6C6D197A54F6.png";

export default function GoogleBusinessPage() {
  const navigate = useNavigate();

  // JSON-LD for Local Business
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Resume Surgeon",
    "description": "Professional ATS-friendly resume services with personal review on every project. Scope confirmed before billing.",
    "url": "https://resumesurgeon.net",
    "logo": LOGO_URL,
    "priceRange": "$59-$149",
    "openingHours": "Mo-Fr 09:00-18:00",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Resume Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Resume Refresh",
            "description": "ATS-friendly formatting cleanup, wording and grammar polish, cleaner layout and readability"
          },
          "price": "59.00",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Resume Rewrite",
            "description": "Full resume rewrite with ATS-friendly layout, achievement-focused bullets, and keyword alignment"
          },
          "price": "99.00",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Job Search Bundle",
            "description": "Resume rewrite, cover letter draft, LinkedIn headline and About rewrite"
          },
          "price": "149.00",
          "priceCurrency": "USD"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* JSON-LD Schema */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">
              Local Resume Services
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Professional ATS-Friendly <br />Resume Help Near You
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Get your resume professionally refined with personal review on every project. ATS-optimized, scope confirmed before billing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/start')}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
                data-testid="local-start-btn"
              >
                Start Your Intake
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => navigate('/audit')}
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
              >
                Free Resume Audit
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="max-w-6xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6 rounded-2xl text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Star className="h-6 w-6 text-teal-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Expert Writers</h3>
              <p className="text-slate-600 text-sm">Real human experts with HR and recruiting backgrounds</p>
            </Card>
            <Card className="p-6 rounded-2xl text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="h-6 w-6 text-teal-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">Fast Turnaround</h3>
              <p className="text-slate-600 text-sm">Most orders delivered within 24-48 hours</p>
            </Card>
            <Card className="p-6 rounded-2xl text-center">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Check className="h-6 w-6 text-teal-700" />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">ATS Optimized</h3>
              <p className="text-slate-600 text-sm">Resumes designed to pass applicant tracking systems</p>
            </Card>
          </div>
        </section>

        {/* Services */}
        <section className="max-w-4xl mx-auto px-6 py-8">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Our Resume Services
          </h2>
          
          <div className="space-y-4">
            {[
              { name: 'Resume Refresh', price: 'From $59', desc: 'ATS-friendly formatting cleanup, wording and grammar polish' },
              { name: 'Resume Rewrite', price: 'From $99', desc: 'Full resume rewrite with achievement-focused bullets and keyword alignment' },
              { name: 'Job Search Bundle', price: 'From $149', desc: 'Resume rewrite, cover letter draft, LinkedIn headline + About rewrite' },
              { name: 'Application Support', price: 'From $99', desc: 'Structured help with up to 10 job applications' },
            ].map((service, i) => (
              <Card key={i} className="p-4 rounded-xl flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-slate-900">{service.name}</h4>
                  <p className="text-sm text-slate-600">{service.desc}</p>
                </div>
                <div className="text-right">
                  <div className="font-bold text-teal-700 text-lg">{service.price}</div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-teal-600"
                    onClick={() => navigate('/pricing')}
                  >
                    Learn more
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* What We Do */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              What is ATS Resume Optimization?
            </h2>
            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 leading-relaxed mb-4">
                Applicant Tracking Systems (ATS) are software used by most companies to filter resumes before a human ever sees them. 
                Many qualified candidates get rejected simply because their resume format confuses the ATS.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                Our resume experts understand exactly what ATS systems look for. We restructure your resume with:
              </p>
              <ul className="space-y-2 text-slate-600">
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" /> Clean, single-column formatting that ATS can parse</li>
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" /> Strategic keywords from your target job descriptions</li>
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" /> Proper section headers that systems recognize</li>
                <li className="flex items-start gap-2"><Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" /> Quantified achievements that impress human readers too</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-gradient-hero text-white relative overflow-hidden">
            <div className="logo-badge">
              <img src={LOGO_URL} alt="" className="h-8 w-8 rounded-lg" />
            </div>
            <div className="pt-4">
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
                Ready to Get More Interviews?
              </h3>
              <p className="text-white/80 mb-6">
                Submit your intake and we'll review your materials personally before confirming scope and pricing.
              </p>
              <Button 
                onClick={() => navigate('/start')}
                className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
                data-testid="local-final-cta"
              >
                Start Your Intake
              </Button>
            </div>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
